import { useState } from 'react';
import { useLanguage } from '../../i18n';
import type { BackendChecks } from '../../types';

interface BackendChecksSectionProps {
  backendChecks: BackendChecks;
  setBackendChecks: (checks: BackendChecks) => void;
  isDemo?: boolean;
}

// 체크박스 항목 컴포넌트
function CheckItem({
  label,
  checked,
  onChange,
  disabled = false,
}: {
  label: string;
  checked?: boolean;
  onChange: (value: boolean | undefined) => void;
  disabled?: boolean;
}) {
  const { t } = useLanguage();
  
  // 3-state: undefined -> true -> false -> undefined
  const handleClick = () => {
    if (checked === undefined) onChange(true);
    else if (checked === true) onChange(false);
    else onChange(undefined);
  };
  
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200
        border
        ${checked === true
          ? 'bg-status-go/10 border-status-go/50 text-status-go'
          : checked === false
            ? 'bg-status-danger/10 border-status-danger/50 text-status-danger'
            : 'bg-terminal-dim/30 border-terminal-border text-terminal-muted hover:border-terminal-muted'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <span className="w-5 h-5 flex items-center justify-center text-xs">
        {checked === true ? '✓' : checked === false ? '✗' : '○'}
      </span>
      <span className="flex-1 text-left">{label}</span>
      <span className="text-[10px] opacity-60">
        {checked === true 
          ? (t.backendChecks?.yes || '예') 
          : checked === false 
            ? (t.backendChecks?.no || '아니오') 
            : (t.backendChecks?.notSet || '미설정')}
      </span>
    </button>
  );
}

// 텍스트 입력 항목 컴포넌트
function TextItem({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
}: {
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-terminal-muted">{label}</label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        placeholder={placeholder}
        disabled={disabled}
        className="terminal-input text-sm"
      />
    </div>
  );
}

// 숫자 입력 항목 컴포넌트
function NumberItem({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
}: {
  label: string;
  value?: number;
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-terminal-muted">{label}</label>
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : undefined)}
        placeholder={placeholder}
        disabled={disabled}
        className="terminal-input text-sm"
        min={0}
      />
    </div>
  );
}

// 섹션 토글 컴포넌트
function Section({
  title,
  description,
  icon,
  color,
  children,
  defaultOpen = false,
}: {
  title: string;
  description: string;
  icon: string;
  color: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className={`border rounded-lg overflow-hidden transition-all ${color}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-terminal-dim/20 transition-colors"
      >
        <span className="text-lg">{icon}</span>
        <div className="flex-1">
          <div className="font-medium text-terminal-text">{title}</div>
          <div className="text-xs text-terminal-muted">{description}</div>
        </div>
        <span className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 pt-2 space-y-3 border-t border-terminal-border/50">
          {children}
        </div>
      )}
    </div>
  );
}

function BackendChecksSection({
  backendChecks,
  setBackendChecks,
  isDemo = false,
}: BackendChecksSectionProps) {
  const { t } = useLanguage();
  const bt = t.backendChecks;
  
  // 헬퍼 함수: 중첩 객체 업데이트
  const updateCheck = <K extends keyof BackendChecks>(
    category: K,
    field: keyof NonNullable<BackendChecks[K]>,
    value: unknown
  ) => {
    setBackendChecks({
      ...backendChecks,
      [category]: {
        ...backendChecks[category],
        [field]: value,
      },
    });
  };

  return (
    <div className="card p-4" data-testid="backend-checks-section">
      <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide flex items-center gap-2">
        <span>⚙️</span>
        <span># {bt?.title || '백엔드 체크리스트'}</span>
      </div>
      <p className="text-xs text-terminal-dim mb-4">
        {bt?.subtitle || '백엔드 릴리즈를 위한 필수 체크 항목입니다'}
      </p>
      
      <div className="space-y-3">
        {/* API 계약/문서화 */}
        <Section
          title={bt?.apiContract || 'API 계약/문서화'}
          description={bt?.apiContractDesc || 'API 스키마 및 계약 변경사항'}
          icon="📋"
          color="border-blue-500/30"
          defaultOpen={true}
        >
          <div className="grid gap-2">
            <CheckItem
              label={bt?.schemaChanged || '스키마 변경'}
              checked={backendChecks.api_contract?.schema_changed}
              onChange={(v) => updateCheck('api_contract', 'schema_changed', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.breakingChange || 'Breaking Change'}
              checked={backendChecks.api_contract?.breaking_change}
              onChange={(v) => updateCheck('api_contract', 'breaking_change', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.errorModelDefined || '에러 모델 정의됨'}
              checked={backendChecks.api_contract?.error_model_defined}
              onChange={(v) => updateCheck('api_contract', 'error_model_defined', v)}
              disabled={isDemo}
            />
            <TextItem
              label={bt?.versionStrategy || '버전 전략'}
              value={backendChecks.api_contract?.version_strategy}
              onChange={(v) => updateCheck('api_contract', 'version_strategy', v)}
              placeholder={bt?.versionStrategyPlaceholder || '예: v1 → v2, deprecate 기간 3개월'}
              disabled={isDemo}
            />
          </div>
        </Section>

        {/* 인증/보안 */}
        <Section
          title={bt?.authSecurity || '인증/보안'}
          description={bt?.authSecurityDesc || '인증, 인가, 보안 관련 체크'}
          icon="🔐"
          color="border-red-500/30"
        >
          <div className="grid gap-2">
            <CheckItem
              label={bt?.authChanged || '인증/인가 변경'}
              checked={backendChecks.auth_security?.auth_changed}
              onChange={(v) => updateCheck('auth_security', 'auth_changed', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.rateLimitConfigured || 'Rate Limit 설정됨'}
              checked={backendChecks.auth_security?.rate_limit_configured}
              onChange={(v) => updateCheck('auth_security', 'rate_limit_configured', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.abuseProtection || 'Abuse 방어 적용됨'}
              checked={backendChecks.auth_security?.abuse_protection}
              onChange={(v) => updateCheck('auth_security', 'abuse_protection', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.piiLoggingSafe || 'PII 로그 마스킹됨'}
              checked={backendChecks.auth_security?.pii_logging_safe}
              onChange={(v) => updateCheck('auth_security', 'pii_logging_safe', v)}
              disabled={isDemo}
            />
          </div>
        </Section>

        {/* DB/마이그레이션 */}
        <Section
          title={bt?.dbMigration || 'DB/마이그레이션'}
          description={bt?.dbMigrationDesc || '데이터베이스 및 마이그레이션 체크'}
          icon="🗄️"
          color="border-amber-500/30"
        >
          <div className="grid gap-2">
            <CheckItem
              label={bt?.migrationExists || '마이그레이션 스크립트 존재'}
              checked={backendChecks.db_migration?.migration_exists}
              onChange={(v) => updateCheck('db_migration', 'migration_exists', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.rollbackPossible || '롤백 가능'}
              checked={backendChecks.db_migration?.rollback_possible}
              onChange={(v) => updateCheck('db_migration', 'rollback_possible', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.backwardCompatible || '기존 버전과 호환'}
              checked={backendChecks.db_migration?.backward_compatible}
              onChange={(v) => updateCheck('db_migration', 'backward_compatible', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.indexPerformanceChecked || '인덱스/쿼리 성능 확인됨'}
              checked={backendChecks.db_migration?.index_performance_checked}
              onChange={(v) => updateCheck('db_migration', 'index_performance_checked', v)}
              disabled={isDemo}
            />
          </div>
        </Section>

        {/* 신뢰성 */}
        <Section
          title={bt?.reliability || '신뢰성'}
          description={bt?.reliabilityDesc || '장애 대응 및 안정성 체크'}
          icon="🛡️"
          color="border-green-500/30"
        >
          <div className="grid gap-2">
            <CheckItem
              label={bt?.timeoutConfigured || '타임아웃 설정됨'}
              checked={backendChecks.reliability?.timeout_configured}
              onChange={(v) => updateCheck('reliability', 'timeout_configured', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.retryLogic || '재시도 로직 있음'}
              checked={backendChecks.reliability?.retry_logic}
              onChange={(v) => updateCheck('reliability', 'retry_logic', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.circuitBreaker || '서킷브레이커 적용됨'}
              checked={backendChecks.reliability?.circuit_breaker}
              onChange={(v) => updateCheck('reliability', 'circuit_breaker', v)}
              disabled={isDemo}
            />
            <TextItem
              label={bt?.externalDependencyFallback || '외부 의존성 장애 시 동작'}
              value={backendChecks.reliability?.external_dependency_fallback}
              onChange={(v) => updateCheck('reliability', 'external_dependency_fallback', v)}
              placeholder={bt?.externalDependencyFallbackPlaceholder || '예: Redis 다운 시 로컬 캐시 사용'}
              disabled={isDemo}
            />
          </div>
        </Section>

        {/* 관측성 */}
        <Section
          title={bt?.observability || '관측성'}
          description={bt?.observabilityDesc || '모니터링 및 알람 체크'}
          icon="📊"
          color="border-purple-500/30"
        >
          <div className="grid gap-2">
            <CheckItem
              label={bt?.loggingReady || '로깅 준비됨'}
              checked={backendChecks.observability?.logging_ready}
              onChange={(v) => updateCheck('observability', 'logging_ready', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.metricsReady || '메트릭 준비됨'}
              checked={backendChecks.observability?.metrics_ready}
              onChange={(v) => updateCheck('observability', 'metrics_ready', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.tracingReady || '트레이싱 준비됨'}
              checked={backendChecks.observability?.tracing_ready}
              onChange={(v) => updateCheck('observability', 'tracing_ready', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.alertingConfigured || '알람 설정됨'}
              checked={backendChecks.observability?.alerting_configured}
              onChange={(v) => updateCheck('observability', 'alerting_configured', v)}
              disabled={isDemo}
            />
            <CheckItem
              label={bt?.sentryIntegrated || 'Sentry 연동됨'}
              checked={backendChecks.observability?.sentry_integrated}
              onChange={(v) => updateCheck('observability', 'sentry_integrated', v)}
              disabled={isDemo}
            />
          </div>
        </Section>

        {/* 성능 */}
        <Section
          title={bt?.performance || '성능'}
          description={bt?.performanceDesc || '성능 목표 및 테스트'}
          icon="⚡"
          color="border-cyan-500/30"
        >
          <div className="grid gap-2">
            <div className="grid grid-cols-2 gap-3">
              <NumberItem
                label={bt?.p95TargetMs || 'p95 목표 (ms)'}
                value={backendChecks.performance?.p95_target_ms}
                onChange={(v) => updateCheck('performance', 'p95_target_ms', v)}
                placeholder="200"
                disabled={isDemo}
              />
              <NumberItem
                label={bt?.p99TargetMs || 'p99 목표 (ms)'}
                value={backendChecks.performance?.p99_target_ms}
                onChange={(v) => updateCheck('performance', 'p99_target_ms', v)}
                placeholder="500"
                disabled={isDemo}
              />
            </div>
            <TextItem
              label={bt?.loadCondition || '부하 조건'}
              value={backendChecks.performance?.load_condition}
              onChange={(v) => updateCheck('performance', 'load_condition', v)}
              placeholder={bt?.loadConditionPlaceholder || '예: 동시 100명, 초당 1000 요청'}
              disabled={isDemo}
            />
            <TextItem
              label={bt?.loadTestResult || '부하 테스트 결과'}
              value={backendChecks.performance?.load_test_result}
              onChange={(v) => updateCheck('performance', 'load_test_result', v)}
              placeholder={bt?.loadTestResultPlaceholder || '예: p95 120ms, 에러율 0.01%'}
              disabled={isDemo}
            />
          </div>
        </Section>
      </div>
    </div>
  );
}

export default BackendChecksSection;
